import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useLocale } from '../context/LocaleContext';
import { LANGUAGES } from '../i18n/translations';

const COUNTRY_NAMES = {
  IN: { en: 'India', hi: 'India', native: 'भारत' },
  DE: { en: 'Germany', native: 'Deutschland' },
  AT: { en: 'Austria', native: 'Österreich' },
  CH: { en: 'Switzerland', native: 'Schweiz' },
  FR: { en: 'France', native: 'France' },
  BE: { en: 'Belgium', native: 'Belgique' },
  ES: { en: 'Spain', native: 'España' },
  MX: { en: 'Mexico', native: 'México' },
  GB: { en: 'United Kingdom', native: 'UK' },
  US: { en: 'United States', native: 'USA' },
  IT: { en: 'Italy', native: 'Italia' },
  NL: { en: 'Netherlands', native: 'Nederland' }
};

const GREETINGS = {
  en: 'Hi', de: 'Hallo', fr: 'Bonjour', es: '¡Hola'
};

const FLAG_BY_COUNTRY = {
  IN: '🇮🇳', DE: '🇩🇪', AT: '🇦🇹', CH: '🇨🇭',
  FR: '🇫🇷', BE: '🇧🇪', ES: '🇪🇸', MX: '🇲🇽',
  GB: '🇬🇧', US: '🇺🇸', IT: '🇮🇹', NL: '🇳🇱'
};

const CountryBanner = () => {
  const { detectedCountry, language, deliveryDays, t } = useLocale();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!detectedCountry) return;
    const dismissed = localStorage.getItem('feron_country_banner_dismissed');
    if (!dismissed) setVisible(true);
  }, [detectedCountry]);

  if (!visible || !detectedCountry) return null;

  const countryInfo = COUNTRY_NAMES[detectedCountry];
  const countryName = countryInfo ? (countryInfo.native || countryInfo.en) : detectedCountry;
  const flag = FLAG_BY_COUNTRY[detectedCountry] || '🌐';
  const greeting = GREETINGS[language] || GREETINGS.en;

  const isIndia = detectedCountry === 'IN';
  const messages = isIndia
    ? {
        en: `${greeting} ${countryName}! Made in India — fast delivery in just ${deliveryDays} days.`,
        de: `${greeting} ${countryName}! Made in India — schnelle Lieferung in nur ${deliveryDays} Tagen.`,
        fr: `${greeting} ${countryName} ! Fabriqué en Inde — livraison rapide en seulement ${deliveryDays} jours.`,
        es: `${greeting} ${countryName}! Hecho en India — entrega rápida en solo ${deliveryDays} días.`
      }
    : {
        en: `${greeting} ${countryName}! Shipping to your country in ${deliveryDays} business days.`,
        de: `${greeting} ${countryName}! Lieferung in Ihr Land in ${deliveryDays} Werktagen.`,
        fr: `${greeting} ${countryName} ! Livraison chez vous en ${deliveryDays} jours ouvrés.`,
        es: `${greeting} ${countryName}! Envío a tu país en ${deliveryDays} días laborables.`
      };
  const message = messages[language] || messages.en;

  return (
    <div className="bg-gradient-to-r from-[#22c55e]/10 via-[#39ff14]/10 to-[#22c55e]/10 border-b border-[#22c55e]/30">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl leading-none">{flag}</span>
          <span className="text-sm font-semibold text-gray-800">{message}</span>
        </div>
        <button
          onClick={() => { setVisible(false); localStorage.setItem('feron_country_banner_dismissed', '1'); }}
          aria-label="dismiss"
          className="text-gray-500 hover:text-[#22c55e] transition"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default CountryBanner;
